import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence, Reorder, useDragControls } from "framer-motion";
import { YoutubeIcon } from "lucide-react";

// ─── 🔧 DEV TOGGLE ───────────────────────────────────────────────────────────
// Set to `true` to skip the audio-proxy and always use the YT IFrame player.
// Set to `false` (default) to use the audio-proxy with IFrame as fallback.
const FORCE_IFRAME = true;
// ─────────────────────────────────────────────────────────────────────────────

// ─── Silent audio keepalive (FORCE_IFRAME background audio on mobile) ────────
// When FORCE_IFRAME is on, the <audio> element plays a silent looping audio
// so the OS keeps the audio/media session alive when the screen locks.
// Without an active <audio> element, iOS and Android will suspend the YT
// IFrame the moment the screen locks.
//
// We build a Blob URL from the raw bytes of the shortest valid silent MP3
// (the same technique used by Howler.js). This is more reliable than a
// data: URI because the browser can actually stream-loop a blob URL.
function makeSilentAudioUrl() {
  // Build a ~1 second silent MP3 from 38 repeated silent MPEG frames
  // (each frame = 26ms @ 44100Hz). More frames = more reliable looping on iOS.
  // Frame bytes: sync word (0xFF 0xE3) + MPEG1 Layer3 header + zero payload.
  const frame = new Uint8Array([
    0xFF,0xE3,0x18,0xC4, 0x00,0x00,0x00,0x03,
    0x48,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00, 0x00,0x00,0x00,0x00,
    0x00,0x00,0x00,0x00,
  ]);
  const buf = new Uint8Array(frame.length * 38);
  for (let i = 0; i < 38; i++) buf.set(frame, i * frame.length);
  return URL.createObjectURL(new Blob([buf], { type: "audio/mpeg" }));
}
// Created once at module load time so we always reuse the same URL.
const SILENT_AUDIO_SRC = makeSilentAudioUrl();
// ─────────────────────────────────────────────────────────────────────────────

// ─── Curated Playlist ────────────────────────────────────────────────────────
const BASE_PLAYLIST = [
  // { id: "jfKfPfyJRdk", title: "lofi hip hop radio", artist: "Lofi Girl" },
  // { id: "5yx6BWlEVcY", title: "synthwave radio", artist: "Lofi Girl" },
  { id: "nCFawchkgrw", title: "non stop lofi mashup", artist: "Soulful Town" },
  // { id: "4xDzrJKXOOY", title: "synthwave retrowave mix", artist: "Chillhop Music" },
  // { id: "35ExI0fwxUY", title: "alice in chains unplugged", artist: "Alice In Chains" },
  { id: "b-bK2Vn3D38", title: "best radio 1 pop hits", artist: "Radio Mix" },
  // { id: "1ZYbU82GVz4", title: "best of hindi songs", artist: "T-series Hits" },
];
const BASE_IDS = new Set(BASE_PLAYLIST.map((t) => t.id));

const BASE_THUMB_COLORS = [
  { bg: "#1a1a2e", accent: "#e94560", emoji: "🎵" },
  { bg: "#0f3460", accent: "#533483", emoji: "🌌" },
  { bg: "#16213e", accent: "#0f3460", emoji: "🎶" },
  { bg: "#2d1b69", accent: "#e040fb", emoji: "🎸" },
  { bg: "#1a0a00", accent: "#b71c1c", emoji: "🔗" },
  { bg: "#0a1628", accent: "#1565c0", emoji: "📻" },
  { bg: "#1a0033", accent: "#6a1b9a", emoji: "🎤" },
];

const BASE_PLAYLIST_WITH_META = BASE_PLAYLIST.map((t, i) => ({
  ...t,
  _placeholderColor: BASE_THUMB_COLORS[i],
}));

const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const INITIAL_PLAYLIST = shuffleArray(BASE_PLAYLIST_WITH_META);

const fmtDuration = (secs) => {
  if (!secs || isNaN(secs)) return null;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const BAR_CONFIGS = Array.from({ length: 28 }, () => ({
  scaleA: Math.random() * 0.7 + 0.3,
  scaleB: Math.random() * 1 + 0.2,
  duration: 0.8 + Math.random() * 0.8,
  opacity: 0.5 + Math.random() * 0.5,
}));

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function useMediaSession({ track, isPlaying, onPlay, onPause, onNext, onPrev }) {
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title:  track?.title  ?? "Unknown Track",
      artist: track?.artist ?? "",
      album:  "",
      artwork: track?.thumbnail
        ? [{ src: track.thumbnail, sizes: "320x180", type: "image/jpeg" }]
        : [],
    });
  }, [track?.id, track?.title, track?.artist, track?.thumbnail]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
  }, [isPlaying]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const handlers = { play: onPlay, pause: onPause, nexttrack: onNext, previoustrack: onPrev };
    for (const [action, handler] of Object.entries(handlers)) {
      try { navigator.mediaSession.setActionHandler(action, handler); } catch (_) {}
    }
    return () => {
      for (const action of Object.keys(handlers)) {
        try { navigator.mediaSession.setActionHandler(action, null); } catch (_) {}
      }
    };
  }, [onPlay, onPause, onNext, onPrev]);
}

const parseLrc = (lrc) => {
  if (!lrc) return [];
  const lines = [];
  for (const raw of lrc.split("\n")) {
    const match = raw.match(/^\[(\d{1,3}):(\d{2})\.(\d{1,3})\]\s*(.*)/);
    if (match) {
      const mins = parseInt(match[1], 10);
      const secs = parseInt(match[2], 10);
      const ms   = parseInt(match[3].padEnd(3, "0"), 10);
      const time = mins * 60 + secs + ms / 1000;
      const text = match[4].trim();
      if (text) lines.push({ time, text });
    }
  }
  return lines;
};

const cleanTrackTitle = (raw) => {
  let t = raw ?? "";
  t = t.replace(/[\(\[【][^\)\]】]*[\)\]】]/g, "");
  t = t.replace(/(official|video|audio|lyrics?|music|hd|hq|4k|live|version|remaster(ed)?|explicit|clean|full|album|single|feat\.?|ft\.?)/gi, "");
  const dashIdx = t.indexOf(" - ");
  if (dashIdx !== -1) t = t.slice(dashIdx + 3);
  t = t.replace(/\s{2,}/g, " ").trim();
  return t || raw;
};

// ─── Visualizer ──────────────────────────────────────────────────────────────
const Visualizer = ({ isPlaying, barCount = 28, height = 32 }) => (
  <div className="flex items-end gap-[2px]" style={{ height }}>
    {BAR_CONFIGS.slice(0, barCount).map((cfg, i) => (
      <motion.div
        key={i}
        className="rounded-full"
        style={{ background: `hsl(var(--primary) / ${cfg.opacity})`, originY: 1, height, width: barCount > 28 ? 4 : 3, flexShrink: 0 }}
        animate={
          isPlaying
            ? { scaleY: [0.15, cfg.scaleA, cfg.scaleB, 0.15], opacity: [0.5, 1, 0.8, 0.5] }
            : { scaleY: 0.1, opacity: 0.3 }
        }
        transition={
          isPlaying
            ? { duration: cfg.duration, repeat: Infinity, ease: "easeInOut", delay: (i / barCount) * 0.4 }
            : { duration: 0.3 }
        }
      />
    ))}
  </div>
);

const FloatingToast = ({ message, icon, visible }) => (
  <AnimatePresence>
    {visible && (
      <motion.div
        initial={{ opacity: 0, y: 6, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 4, scale: 0.94 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        className="absolute left-0 right-0 flex items-center justify-center pointer-events-none"
        style={{ bottom: "calc(100% + 8px)", zIndex: 1 }}
      >
        <div
          className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-[11px] font-semibold max-w-[260px]"
          style={{
            background: "hsl(var(--surface-1) / 0.97)",
            border: "1px solid hsl(var(--primary) / 0.3)",
            color: "hsl(var(--primary))",
            boxShadow: "0 4px 20px hsl(var(--primary) / 0.2), 0 1px 4px hsl(0 0% 0% / 0.12)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          }}
        >
          {icon && <span className="shrink-0">{icon}</span>}
          <span className="truncate">{message}</span>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── Mobile Toast (fixed position, used inside MobilePlayer) ─────────────────
const MobileToast = ({ message, icon, visible }) => (
  <AnimatePresence>
    {visible && (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.94 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        className="fixed left-0 right-0 flex items-center justify-center pointer-events-none"
        style={{ bottom: 32, zIndex: 10030 }}
      >
        <div
          className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl text-sm font-semibold max-w-[280px]"
          style={{
            background: "hsl(var(--surface-1) / 0.97)",
            border: "1px solid hsl(var(--primary) / 0.35)",
            color: "hsl(var(--primary))",
            boxShadow: "0 8px 32px hsl(var(--primary) / 0.25), 0 2px 8px hsl(0 0% 0% / 0.2)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
          }}
        >
          {icon && <span className="shrink-0 text-base">{icon}</span>}
          <span className="truncate">{message}</span>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const PlaceholderThumb = ({ track, size = 36 }) => {
  const meta = track?._placeholderColor;
  if (!meta) {
    return (
      <div className="shrink-0 rounded-md flex items-center justify-center"
        style={{ width: size, height: size, background: "hsl(var(--surface-3))", border: "1px solid hsl(var(--border) / 0.4)" }}>
        <span style={{ fontSize: size * 0.45, opacity: 0.35 }}>🎵</span>
      </div>
    );
  }
  return (
    <div
      className="shrink-0 rounded-md flex items-center justify-center relative overflow-hidden"
      style={{ width: size, height: size, background: meta.bg, border: "1px solid hsl(var(--border) / 0.3)" }}
    >
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(circle at 70% 30%, ${meta.accent}55 0%, transparent 65%)`,
      }} />
      <span style={{ fontSize: size * 0.42, position: "relative", zIndex: 1 }}>{meta.emoji}</span>
    </div>
  );
};

const Thumb = ({ src, alt, size = 36, track }) => {
  const [err, setErr] = useState(false);
  if ((err || !src) && track?._placeholderColor) return <PlaceholderThumb track={track} size={size} />;
  if (err || !src) {
    return (
      <div className="shrink-0 rounded-md flex items-center justify-center"
        style={{ width: size, height: size, background: "hsl(var(--surface-3))", border: "1px solid hsl(var(--border) / 0.4)" }}>
        <span style={{ fontSize: size * 0.45, opacity: 0.35 }}>🎵</span>
      </div>
    );
  }
  return (
    <img src={src} alt={alt} width={size} height={size} onError={() => setErr(true)}
      className="rounded-md object-cover shrink-0"
      style={{ width: size, height: size, border: "1px solid hsl(var(--border) / 0.3)" }} />
  );
};

// ─── Seekbar ─────────────────────────────────────────────────────────────────
const Seekbar = ({ currentTime, duration, onSeek, isLive, height = 4 }) => {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const durationRef = useRef(duration);
  const onSeekRef = useRef(onSeek);
  const [dragging, setDragging] = useState(false);
  const [dragPct, setDragPct] = useState(null);
  const [hoverPct, setHoverPct] = useState(null);

  useEffect(() => { durationRef.current = duration; }, [duration]);
  useEffect(() => { onSeekRef.current = onSeek; }, [onSeek]);

  const clientXFromEvent = (e) => {
    if (e.changedTouches?.length) return e.changedTouches[0].clientX;
    if (e.touches?.length) return e.touches[0].clientX;
    return e.clientX;
  };

  const pctFromClientX = (clientX) => {
    if (!trackRef.current) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onTouchStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      draggingRef.current = true;
      setDragging(true);
      setDragPct(pctFromClientX(clientXFromEvent(e)));
    };
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    return () => el.removeEventListener("touchstart", onTouchStart);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      if (e.cancelable) e.preventDefault();
      setDragPct(pctFromClientX(clientXFromEvent(e)));
    };
    const onUp = (e) => {
      if (!draggingRef.current) return;
      const pct = pctFromClientX(clientXFromEvent(e));
      onSeekRef.current(pct * durationRef.current);
      draggingRef.current = false;
      setDragging(false);
      setDragPct(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    setDragging(true);
    setDragPct(pctFromClientX(e.clientX));
  };

  const displayPct = isLive ? 1 : dragging ? (dragPct ?? 0) : duration > 0 ? currentTime / duration : 0;
  const hoverTime = hoverPct != null && duration > 0 ? fmtDuration(hoverPct * duration) : null;

  return (
    <div
      className="relative group mb-1"
      onPointerDown={(e) => { e.stopPropagation(); }}
      onMouseDown={(e) => e.stopPropagation()}
      style={{ touchAction: "none" }}
    >
      {hoverTime && !isLive && (
        <div
          className="absolute -top-6 text-[9px] font-mono px-1.5 py-0.5 rounded pointer-events-none"
          style={{
            left: `${(hoverPct ?? 0) * 100}%`,
            transform: "translateX(-50%)",
            background: "hsl(var(--surface-1))",
            border: "1px solid hsl(var(--border))",
            color: "hsl(var(--foreground))",
          }}
        >
          {hoverTime}
        </div>
      )}
      <div
        ref={trackRef}
        className="w-full rounded-full cursor-pointer relative"
        style={{ height, background: "hsl(var(--surface-3))", touchAction: "none", userSelect: "none" }}
        onMouseDown={handleMouseDown}
        onMouseMove={(e) => !isLive && setHoverPct(pctFromClientX(e.clientX))}
        onMouseLeave={() => setHoverPct(null)}
      >
        <div
          className="absolute left-0 top-0 h-full rounded-full"
          style={{
            width: `${displayPct * 100}%`,
            background: "var(--gradient-primary)",
            transition: dragging ? "none" : "width 0.25s linear",
          }}
        />
        {!isLive && (
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            style={{
              left: `${displayPct * 100}%`,
              width: height * 2.5, height: height * 2.5,
              background: "hsl(var(--primary))",
              boxShadow: "0 0 0 2px hsl(var(--surface-1))",
              pointerEvents: "none",
            }}
          />
        )}
      </div>
    </div>
  );
};

const TimeDisplay = ({ currentTime, duration, isLive }) => (
  <div className="flex items-center justify-between mb-3" style={{ fontSize: 9, color: "hsl(var(--muted-foreground) / 0.6)", fontFamily: "monospace" }}>
    {isLive ? (
      <span className="flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block animate-pulse" />
        LIVE
      </span>
    ) : (
      <span>{fmtDuration(currentTime) ?? "0:00"}</span>
    )}
    {!isLive && <span>{fmtDuration(duration) ?? "—"}</span>}
  </div>
);

// ─── Lyrics Tab content ───────────────────────────────────────────────────────
const LyricsContent = ({ track, currentTime, syncEnabled = true, onSeek, mobile = false }) => {
  const [state, setState] = useState("idle");
  const [plainLyrics, setPlainLyrics] = useState("");
  const [lrcLines, setLrcLines] = useState([]);
  const [isSynced, setIsSynced] = useState(false);
  const prevTrackId = useRef(null);
  const activeLyricRef = useRef(null);

  useEffect(() => {
    const id = track?.id ?? track?.title;
    if (!id || id === prevTrackId.current) return;
    prevTrackId.current = id;
    if (!track?.title) return;
    setState("loading");
    setPlainLyrics("");
    setLrcLines([]);
    setIsSynced(false);

    const resolveAndFetch = async () => {
      let title = track.title ?? "";
      let artist = track.artist ?? "";
      try {
        const infoRes = await fetch(
          `/api/track-info?video_id=${encodeURIComponent(track.id ?? "")}`,
          { method: "POST" }
        );
        if (infoRes.ok) {
          const info = await infoRes.json();
          if (info.title) title = info.title;
          if (info.artist) artist = info.artist;
        }
      } catch (_) {}

      title = cleanTrackTitle(title);
      const params = new URLSearchParams({ title, artist });
      const r = await fetch(`/api/lyrics?${params}`, { method: "POST" });
      const data = await r.json();
      if (data.found && data.lyrics) {
        if (data.synced && data.lrc) {
          const parsed = parseLrc(data.lrc);
          if (parsed.length > 0) {
            setLrcLines(parsed);
            setIsSynced(true);
            setState("found");
            return;
          }
        }
        setPlainLyrics(data.lyrics);
        setIsSynced(false);
        setState("found");
      } else {
        setState("notfound");
      }
    };
    resolveAndFetch().catch(() => setState("error"));
  }, [track?.id, track?.title]);

  const activeLineIdx = syncEnabled && isSynced && lrcLines.length > 0
    ? lrcLines.reduce((best, line, i) => line.time <= currentTime ? i : best, -1)
    : -1;

  useEffect(() => {
    if (activeLyricRef.current) {
      activeLyricRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeLineIdx]);

  if (state === "idle" || state === "loading") {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        {state === "loading" ? (
          <>
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-5 h-5 rounded-full"
              style={{ border: "2px solid hsl(var(--primary) / 0.25)", borderTopColor: "hsl(var(--primary))" }}
            />
            <span className="text-[11px]" style={{ color: "hsl(var(--muted-foreground))" }}>Fetching lyrics…</span>
          </>
        ) : (
          <span className="text-[11px]" style={{ color: "hsl(var(--muted-foreground) / 0.5)" }}>Switch to this tab to load lyrics</span>
        )}
      </div>
    );
  }

  if (state === "notfound") return (
    <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
      <span className="text-2xl">🎵</span>
      <div className="text-xs font-semibold" style={{ color: "hsl(var(--foreground))" }}>No lyrics found</div>
      <div className="text-[10px]" style={{ color: "hsl(var(--muted-foreground))" }}>Try a more specific track name</div>
    </div>
  );

  if (state === "error") return (
    <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
      <span className="text-2xl">⚠️</span>
      <div className="text-xs font-semibold" style={{ color: "hsl(var(--foreground))" }}>Couldn't fetch lyrics</div>
      <div className="text-[10px]" style={{ color: "hsl(var(--muted-foreground))" }}>Check API server is running</div>
    </div>
  );

  const activeSize = mobile ? 15 : 13;
  const inactiveSize = mobile ? 13 : 11;

  if (syncEnabled && isSynced && lrcLines.length > 0) {
    return (
      <div className="space-y-1 pb-2">
        {lrcLines.map((line, i) => {
          const isActive = i === activeLineIdx;
          const isPast = i < activeLineIdx;
          return (
            <div
              key={i}
              ref={isActive ? activeLyricRef : null}
              onClick={() => onSeek?.(line.time)}
              className="text-center px-2 py-0.5 rounded-lg transition-all duration-300"
              style={{
                fontSize: isActive ? activeSize : inactiveSize,
                fontWeight: isActive ? 700 : 400,
                color: isActive ? "hsl(var(--primary))" : isPast ? "hsl(var(--muted-foreground) / 0.4)" : "hsl(var(--foreground) / 0.65)",
                background: isActive ? "hsl(var(--primary) / 0.08)" : "transparent",
                lineHeight: 1.6,
                transform: isActive ? "scale(1.02)" : "scale(1)",
                letterSpacing: isActive ? "0.01em" : "normal",
                wordBreak: "break-word",
                whiteSpace: "normal",
                overflowWrap: "break-word",
                cursor: onSeek ? "pointer" : "default",
              }}
            >
              {line.text}
            </div>
          );
        })}
        {!mobile && (
          <div className="pt-3 text-center text-[9px]" style={{ color: "hsl(var(--muted-foreground) / 0.35)" }}>
            ✦ synced · powered by syncedlyrics
          </div>
        )}
      </div>
    );
  }

  const displayPlain = plainLyrics || lrcLines.map((l) => l.text).join("\n");
  return (
    <div className="space-y-0.5 pb-2">
      {displayPlain.split("\n").map((line, i) => (
        <div key={i} className="text-center leading-relaxed"
          style={{
            fontSize: mobile ? 13 : 11,
            color: line.trim() ? "hsl(var(--foreground))" : "transparent",
            minHeight: "1rem",
            wordBreak: "break-word",
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}>
          {line.trim() || "\u00A0"}
        </div>
      ))}
      {!mobile && (
        <div className="pt-3 text-center text-[9px]" style={{ color: "hsl(var(--muted-foreground) / 0.35)" }}>
          powered by syncedlyrics
        </div>
      )}
    </div>
  );
};

// ─── Context Menu ─────────────────────────────────────────────────────────────
const ContextMenu = ({ x, y, track, isBase, isActive, isPlaying, source, onClose, onPlayNow, onPlayNext, onAddToQueue, onRemove, onOpenYoutube, onCopyUrl }) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handleOutside, true);
    document.addEventListener("touchstart", handleOutside, true);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleOutside, true);
      document.removeEventListener("touchstart", handleOutside, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  const menuW = 212, menuH = 300;
  const cx = Math.min(x, window.innerWidth - menuW - 8);
  const cy = Math.min(y, window.innerHeight - menuH - 8);

  const Item = ({ icon, label, onClick, danger, disabled, sub }) => (
    <button
      onClick={() => { if (!disabled) { onClick(); onClose(); } }}
      disabled={disabled}
      className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[11px] text-left transition-colors disabled:opacity-30 disabled:cursor-default"
      style={{ color: danger ? "hsl(var(--destructive))" : "hsl(var(--foreground))" }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = danger ? "hsl(var(--destructive) / 0.09)" : "hsl(var(--primary) / 0.08)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <span className="w-4 text-center shrink-0 leading-none" style={{ fontSize: 13 }}>{icon}</span>
      <span className="flex-1">{label}</span>
      {sub && <span className="text-[9px] opacity-40 ml-1">{sub}</span>}
    </button>
  );
  const Sep = () => <div className="my-1 mx-2" style={{ height: 1, background: "hsl(var(--border) / 0.5)" }} />;

  const isQueueSource = source === "queue";
  const isSearchSource = source === "search";
  const canRemove = isQueueSource &&  !isActive;

  return (
    <motion.div ref={menuRef}
      initial={{ opacity: 0, scale: 0.88, y: -6 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.88, y: -6 }}
      transition={{ duration: 0.13, ease: [0.16, 1, 0.3, 1] }}
      className="fixed z-[11020] rounded-xl p-1.5"
      style={{ left: cx, top: cy, width: menuW, background: "hsl(var(--surface-1) / 0.99)", border: "1px solid hsl(var(--primary) / 0.16)", backdropFilter: "blur(32px)", WebkitBackdropFilter: "blur(32px)", boxShadow: "0 8px 36px hsl(0 0% 0% / 0.28)" }}
    >
      <div className="px-2.5 py-2.5 mb-0.5 flex items-center gap-2.5" style={{ borderBottom: "1px solid hsl(var(--border) / 0.45)" }}>
        <Thumb src={track.thumbnail} alt={track.title} size={34} track={track} />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold truncate leading-tight" style={{ color: "hsl(var(--foreground))" }}>{track.title}</div>
          <div className="text-[10px] truncate mt-0.5 opacity-55" style={{ color: "hsl(var(--muted-foreground))" }}>{track.artist}</div>
        </div>
      </div>
      <Item icon="▶" label="Play now" onClick={onPlayNow} disabled={isActive && isPlaying} sub={isActive && isPlaying ? "playing" : ""} />
      {isSearchSource && (
        <>
          <Item icon="⏭" label="Play next" onClick={onPlayNext} />
          <Item icon="＋" label="Add to queue" onClick={onAddToQueue} />
        </>
      )}
      <Sep />
      <Item icon={<YoutubeIcon style={{ width: 12, height: 12, color: "#FF0000" }} />} label="Open in YouTube" onClick={onOpenYoutube} />
      <Item icon="🔗" label="Copy URL" onClick={onCopyUrl} />
      {canRemove && (
        <>
          <Sep />
          <Item icon="✕" label="Remove from queue" onClick={onRemove} danger />
        </>
      )}
    </motion.div>
  );
};

// ─── Google Cast SDK integration ─────────────────────────────────────────────
const CAST_APP_ID = "CC1AD845";

function useCast({ track, isPlaying }) {
  const [castState, setCastState] = useState("unavailable");
  const [castDevice, setCastDevice] = useState(null);
  const sessionRef = useRef(null);

  useEffect(() => {
    if (document.querySelector('script[src*="cast_sender"]')) { initCastApi(); return; }
    window["__onGCastApiAvailable"] = (isAvailable) => { if (isAvailable) initCastApi(); };
    const script = document.createElement("script");
    script.src = "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const initCastApi = () => {
    try {
      const context = cast.framework.CastContext.getInstance();
      context.setOptions({ receiverApplicationId: CAST_APP_ID, autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED });
      context.addEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, (e) => {
        const s = e.sessionState;
        const SS = cast.framework.SessionState;
        if (s === SS.SESSION_STARTED || s === SS.SESSION_RESUMED) {
          const session = context.getCurrentSession();
          sessionRef.current = session;
          setCastState("connected");
          setCastDevice(session?.getCastDevice()?.friendlyName ?? "Cast device");
        } else if (s === SS.SESSION_START_FAILED || s === SS.SESSION_ENDED) {
          sessionRef.current = null; setCastState("idle"); setCastDevice(null);
        } else if (s === SS.SESSION_STARTING) {
          setCastState("connecting");
        }
      });
      setCastState("idle");
    } catch (e) { setCastState("unavailable"); }
  };

  useEffect(() => {
    const session = sessionRef.current;
    if (castState !== "connected" || !session || !track?.id) return;
    try {
      const mediaInfo = new chrome.cast.media.MediaInfo(`https://www.youtube.com/watch?v=${track.id}`, "video/mp4");
      mediaInfo.metadata = new chrome.cast.media.MusicTrackMediaMetadata();
      mediaInfo.metadata.title = track.title ?? "";
      mediaInfo.metadata.artist = track.artist ?? "";
      if (track.thumbnail) mediaInfo.metadata.images = [new chrome.cast.Image(track.thumbnail)];
      mediaInfo.streamType = chrome.cast.media.StreamType.BUFFERED;
      const req = new chrome.cast.media.LoadRequest(mediaInfo);
      req.autoplay = true;
      session.loadMedia(req).catch(() => {});
    } catch (_) {}
  }, [track?.id, castState]);

  const requestCast = useCallback(async () => {
    try { await cast.framework.CastContext.getInstance().requestSession(); } catch (_) {}
  }, []);

  const stopCast = useCallback(() => {
    try { cast.framework.CastContext.getInstance().endCurrentSession(true); } catch (_) {}
  }, []);

  return { castState, castDevice, requestCast, stopCast };
}

const CastButton = ({ castState, castDevice, onRequest, onStop }) => {
  if (castState === "unavailable") return null;
  const active = castState === "connected";
  const connecting = castState === "connecting";
  return (
    <motion.button onClick={active ? onStop : onRequest} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.92 }}
      title={active ? `Casting to ${castDevice} — click to stop` : connecting ? "Connecting…" : "Cast to a speaker or TV"}
      className="w-9 h-9 rounded-xl flex items-center justify-center relative transition-all"
      style={{ background: active ? "hsl(var(--primary) / 0.15)" : "hsl(var(--surface-2))", color: active ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))", border: active ? "1px solid hsl(var(--primary) / 0.35)" : "1px solid transparent" }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 8.5A19 19 0 0 1 20 8.5" />
        <path d="M2 13A13 13 0 0 1 14 13" />
        <path d="M2 17.5A7 7 0 0 1 8 18" />
        <circle cx="2" cy="20" r="2" fill={active ? "currentColor" : "none"} stroke="currentColor" />
      </svg>
      {connecting && (
        <motion.span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full" style={{ background: "hsl(var(--primary))" }}
          animate={{ opacity: [1, 0.15, 1] }} transition={{ duration: 0.7, repeat: Infinity }} />
      )}
    </motion.button>
  );
};

// ─── QueueItem ────────────────────────────────────────────────────────────────
const DRAG_THRESHOLD = 6;
const LONG_PRESS_MS = 500;

function QueueItem({ track, index, isActive, isPlaying, isBase, onPlay, onContextMenu }) {
  const pointerStartRef = useRef(null);
  const didDragRef = useRef(false);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const clearLongPress = () => { clearTimeout(longPressTimerRef.current); };

  const handlePointerDown = (e) => {
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    didDragRef.current = false;
    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      onContextMenu({ preventDefault: () => {}, stopPropagation: () => {}, clientX: e.clientX, clientY: e.clientY });
    }, LONG_PRESS_MS);
  };

  const handlePointerMove = (e) => {
    if (!pointerStartRef.current) return;
    const dx = e.clientX - pointerStartRef.current.x;
    const dy = e.clientY - pointerStartRef.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
      didDragRef.current = true;
      clearLongPress();
    }
  };

  const handlePointerUp = () => { clearLongPress(); };
  const handleClick = () => { if (didDragRef.current || longPressTriggeredRef.current) return; onPlay(); };

  return (
    <Reorder.Item
      value={track}
      onContextMenu={onContextMenu}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={clearLongPress}
      onClick={handleClick}
      className="rounded-lg flex items-center gap-2 px-2 py-1.5"
      style={{
        background: isActive ? "hsl(var(--primary) / 0.12)" : "transparent",
        color: isActive ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
        border: isActive ? "1px solid hsl(var(--primary) / 0.25)" : "1px solid transparent",
        cursor: "grab", userSelect: "none", WebkitUserSelect: "none",
        touchAction: "pan-y",
      }}
    >
      <span style={{ fontSize: 10, opacity: 0.22, cursor: "grab" }}>⠿</span>
      <span className="w-4 text-center shrink-0" style={{ fontSize: 11 }}>
        {isActive && isPlaying
          ? <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1, repeat: Infinity }}>♪</motion.span>
          : `${index + 1}`}
      </span>
      <div className="relative shrink-0">
        <Thumb src={track.thumbnail} alt={track.title} size={30} track={track} />
        {isActive && isPlaying && (
          <div className="absolute inset-0 rounded-md flex items-center justify-center" style={{ background: "hsl(var(--primary) / 0.5)" }}>
            <motion.span style={{ fontSize: 9, color: "hsl(var(--primary-foreground))" }}
              animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1, repeat: Infinity }}>♪</motion.span>
          </div>
        )}
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className="truncate font-medium leading-tight" style={{ fontSize: 13 }}>{track.title}</span>
        <span className="truncate opacity-55" style={{ fontSize: 11 }}>{track.artist}</span>
      </div>
      {isBase
        ? <span className="px-1 py-0.5 rounded shrink-0 font-semibold" style={{ fontSize: 9, background: "hsl(var(--surface-3))", color: "hsl(var(--muted-foreground) / 0.4)" }}>default</span>
        : <span className="px-1 py-0.5 rounded shrink-0 font-semibold" style={{ fontSize: 9, background: "hsl(var(--secondary) / 0.12)", color: "hsl(var(--secondary) / 0.65)" }}>added</span>}
    </Reorder.Item>
  );
}

// ─── Search Result Row ────────────────────────────────────────────────────────
function SearchResultRow({ r, isActivePlaying, isPlaying, onPlay, onAddNext, onAddEnd, onContextMenu, size = 48, compact = false }) {
  const [hovered, setHovered] = useState(false);
  const dur = fmtDuration(r.duration);
  const pointerStartRef = useRef(null);
const longPressTimerRef = useRef(null);
const longPressTriggeredRef = useRef(false);

const handleTouchStart = (e) => {
  if (!r.id) return;

  e.preventDefault();

  longPressTriggeredRef.current = false;

  const touch = e.touches[0];
  const clientX = touch.clientX;
  const clientY = touch.clientY;

  longPressTimerRef.current = setTimeout(() => {
  longPressTriggeredRef.current = true;

  if (navigator.vibrate) {
    navigator.vibrate([10, 30, 10]);
  }

  onContextMenu?.({
    preventDefault: () => {},
    stopPropagation: () => {},
    clientX,
    clientY,
  });

}, LONG_PRESS_MS);
};

const handleTouchEnd = () => {
  clearTimeout(longPressTimerRef.current);
};

const handleTouchMove = () => {
  clearTimeout(longPressTimerRef.current);
};

  return (
    <motion.div
    onTouchStart={handleTouchStart}
  onTouchEnd={handleTouchEnd}
  onTouchMove={handleTouchMove}
      onClick={() => {
  if (longPressTriggeredRef.current) return;
  r.id && onPlay();
}}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      className={compact ? "flex items-center gap-2 px-2 py-1.5 rounded-xl text-xs group cursor-pointer transition-colors" : "flex items-center gap-3 px-3 py-2.5 rounded-2xl cursor-pointer"}
      style={{
        background: isActivePlaying ? "hsl(var(--primary) / 0.1)" : hovered ? "hsl(var(--surface-2) / 0.8)" : compact ? "hsl(var(--surface-2) / 0.5)" : "hsl(var(--surface-2) / 0.6)",
        border: isActivePlaying ? `1px solid hsl(var(--primary) / ${compact ? 0.2 : 0.25})` : "1px solid transparent",
        transition: "background 0.15s",
        userSelect: "none",
  WebkitUserSelect: "none",
  WebkitTouchCallout: "none",
  touchAction: "manipulation"
      }}
    >
      <div className="relative shrink-0">
        <Thumb src={r.thumbnail} alt={r.title} size={size} />
        {isActivePlaying && isPlaying ? (
          <div className="absolute inset-0 rounded-md flex items-center justify-center" style={{ background: "hsl(var(--primary) / 0.55)" }}>
            <motion.span className={compact ? "text-white text-xs" : "text-white text-sm"}
              animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1, repeat: Infinity }}>♪</motion.span>
          </div>
        ) : (
          <div className="absolute inset-0 rounded-md flex items-center justify-center transition-opacity"
            style={{ background: "hsl(0 0% 0% / 0.5)", opacity: hovered ? 1 : 0, pointerEvents: "none" }}>
            <span style={{ color: "white", fontSize: compact ? 12 : 16, lineHeight: 1 }}>▶</span>
          </div>
        )}
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <span className={`truncate font-semibold leading-tight ${compact ? "text-[11px]" : "text-sm"}`}
          style={{ color: isActivePlaying ? "hsl(var(--primary))" : "hsl(var(--foreground))" }}>
          {r.title}
        </span>
        <div className={`flex items-center gap-${compact ? 1.5 : 2} mt-0.5`}>
          <span className={`truncate opacity-50 ${compact ? "text-[10px]" : "text-xs"}`}>{r.artist}</span>
          {dur && (
            <span className={`font-mono px-1${compact ? "" : ".5"} py-0.5 rounded shrink-0 ${compact ? "text-[9px]" : "text-[10px]"}`}
              style={{ background: "hsl(var(--surface-3))", color: "hsl(var(--muted-foreground) / 0.7)" }}>
              {dur}
            </span>
          )}
        </div>
      </div>
      {r.id && (
        <div
          className={compact ? "flex gap-1 shrink-0 overflow-hidden w-0 group-hover:w-auto opacity-0 group-hover:opacity-100 transition-all" : "flex gap-1.5 shrink-0"}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={(e) => { e.stopPropagation(); onAddNext(); }}
            className={`font-bold rounded-${compact ? "md" : "xl"} ${compact ? "text-[9px] px-1.5 py-0.5" : "text-[11px] px-2.5 py-1.5"}`}
            style={{ background: "hsl(var(--primary) / 0.14)", color: "hsl(var(--primary))" }}>Next</button>
          <button onClick={(e) => { e.stopPropagation(); onAddEnd(); }}
            className={`font-bold rounded-${compact ? "md" : "xl"} ${compact ? "text-[9px] px-1.5 py-0.5" : "text-[11px] px-2.5 py-1.5"}`}
            style={{ background: "hsl(var(--surface-3))", color: "hsl(var(--muted-foreground))" }}>+Q</button>
        </div>
      )}
    </motion.div>
  );
}

function MobileSearchPanel({
  searchQuery, handleSearchInput, clearSearch,
  searchResults, isSearching, searchInputRef,
  isPlaying, activeSearchId, addAfterCurrent, addToEnd, playSearchResult,
  onClose,openCtx,  
}) {
  return (
    <motion.div
      initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
      transition={{ type: "spring", stiffness: 320, damping: 30 }}
      className="absolute inset-x-0 bottom-0 flex flex-col rounded-t-3xl overflow-hidden"
      style={{
        zIndex: 20, maxHeight: "75vh",
        background: "hsl(var(--surface-1) / 0.99)",
        border: "1px solid hsl(var(--primary) / 0.2)", borderBottom: "none",
        backdropFilter: "blur(40px)", WebkitBackdropFilter: "blur(40px)",
        boxShadow: "0 -8px 40px hsl(0 0% 0% / 0.3)",
      }}
    >
      <div className="flex justify-center pt-3 pb-2 shrink-0">
        <div className="w-10 h-1 rounded-full" style={{ background: "hsl(var(--border) / 0.6)" }} />
      </div>
      <div className="flex items-center justify-between px-5 pb-3 shrink-0" style={{ borderBottom: "1px solid hsl(var(--border) / 0.35)" }}>
        <span className="text-sm font-bold" style={{ color: "hsl(var(--foreground))" }}>Search YouTube</span>
        <button onClick={onClose} className="w-7 h-7 rounded-full flex items-center justify-center text-xs"
          style={{ background: "hsl(var(--surface-2))", color: "hsl(var(--muted-foreground))" }}>✕</button>
      </div>
      <div className="px-5 pt-3 pb-2 shrink-0">
        <div className="flex items-center gap-2 rounded-xl px-3 py-2.5"
          style={{ background: "hsl(var(--surface-2))", border: "1px solid hsl(var(--primary) / 0.2)" }}>
          <span className="text-sm" style={{ color: "hsl(var(--muted-foreground))" }}>🔍</span>
          <input ref={searchInputRef} type="text" value={searchQuery} onChange={handleSearchInput}
            placeholder="Search for songs, artists…" className="flex-1 bg-transparent outline-none"
            style={{ color: "hsl(var(--foreground))", caretColor: "hsl(var(--primary))", fontSize: 16, lineHeight: 1.4 }} />
          {searchQuery && <button onClick={clearSearch} className="text-sm opacity-50 hover:opacity-100">✕</button>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 pb-6" style={{ scrollbarWidth: "thin", scrollbarColor: "hsl(var(--primary) / 0.2) transparent" }}>
        {isSearching && (
          <div className="flex justify-center py-6">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
              className="w-6 h-6 rounded-full"
              style={{ border: "2px solid hsl(var(--primary) / 0.3)", borderTopColor: "hsl(var(--primary))" }} />
          </div>
        )}
        {!isSearching && searchQuery && searchResults.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <span className="text-2xl">🔍</span>
            <span className="text-sm" style={{ color: "hsl(var(--muted-foreground))" }}>No results found</span>
          </div>
        )}
        {!isSearching && !searchQuery && (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <span className="text-3xl">🎵</span>
            <span className="text-sm" style={{ color: "hsl(var(--muted-foreground) / 0.6)" }}>Type to search</span>
          </div>
        )}
        {!isSearching && searchResults.length > 0 && (
          <div className="space-y-2 pt-1">
            {searchResults.map((r, i) => {
              const isActivePlaying = activeSearchId === r.id;
              return (
                <SearchResultRow key={r.id ?? i} r={r} isActivePlaying={isActivePlaying} isPlaying={isPlaying}
                  onPlay={() => playSearchResult(r.id)} onAddNext={() => addAfterCurrent(r)} onAddEnd={() => addToEnd(r)}
                  size={48} compact={false} onContextMenu={(e) => openCtx(e, r, i, "search")}/>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Tab Bar ──────────────────────────────────────────────────────────────────
const TabBar = ({ tabs, activeTab, setActiveTab, borderStyle = {}, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? "" : "justify-center"} px-0 shrink-0`}
    style={{ borderBottom: "1px solid hsl(var(--border) / 0.4)", ...borderStyle }}>
    {tabs.map((tab) => {
      const isActive = activeTab === tab.id;
      return (
        <button key={tab.id} onClick={() => setActiveTab(tab.id)}
          className={`flex items-center justify-center gap-1.5 py-2.5 font-semibold rounded-t-lg transition-all ${fullWidth ? "flex-1" : "px-3"}`}
          style={{
            fontSize: fullWidth ? 12 : 11,
            color: isActive ? "hsl(var(--primary))" : "hsl(var(--muted-foreground) / 0.6)",
            borderBottom: isActive ? "2px solid hsl(var(--primary))" : "2px solid transparent",
            marginBottom: -1,
            background: isActive ? "hsl(var(--primary) / 0.05)" : "transparent",
          }}>
          <span style={{ fontSize: fullWidth ? 14 : 12 }}>{tab.icon}</span>
          {tab.label}
        </button>
      );
    })}
  </div>
);

// ─── Mobile Mini Player (collapsed pill) ─────────────────────────────────────
function MobileMiniPlayer({ nowPlayingTrack, isPlaying, togglePlay, onExpand, onClose }) {
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: "spring", stiffness: 320, damping: 28 }}
      className="fixed bottom-0 left-0 right-0"
      style={{ zIndex: 10001, paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div
        className="mx-3 mb-3 rounded-2xl flex items-center gap-3 px-4 py-3"
        style={{
          background: "hsl(var(--surface-1) / 0.97)",
          border: "1px solid hsl(var(--primary) / 0.25)",
          backdropFilter: "blur(32px)",
          WebkitBackdropFilter: "blur(32px)",
          boxShadow: isPlaying
            ? "0 -4px 24px hsl(var(--primary) / 0.2), 0 8px 32px hsl(0 0% 0% / 0.3)"
            : "0 4px 24px hsl(0 0% 0% / 0.2)",
        }}
      >
        {/* Tap area to expand */}
        <button className="flex items-center gap-3 flex-1 min-w-0" onClick={onExpand}>
          <div className="relative shrink-0">
            <Thumb src={nowPlayingTrack?.thumbnail} alt={nowPlayingTrack?.title} size={40} track={nowPlayingTrack} />
            {isPlaying && (
              <motion.div
                className="absolute inset-0 rounded-md"
                animate={{ boxShadow: ["0 0 0px hsl(var(--primary) / 0)", "0 0 8px hsl(var(--primary) / 0.5)", "0 0 0px hsl(var(--primary) / 0)"] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            )}
          </div>
          <div className="flex flex-col min-w-0 flex-1 text-left">
            <span className="text-sm font-semibold truncate leading-tight" style={{ color: "hsl(var(--foreground))" }}>
              {nowPlayingTrack?.title ?? "—"}
            </span>
            <span className="text-xs truncate mt-0.5" style={{ color: "hsl(var(--muted-foreground))" }}>
              {nowPlayingTrack?.artist ?? ""}
            </span>
          </div>
          {/* Mini visualizer */}
          <div className="flex items-end gap-[2px] shrink-0" style={{ height: 18 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <motion.div key={i} className="w-[3px] rounded-full"
                style={{ background: "hsl(var(--primary))", height: 18, originY: 1 }}
                animate={isPlaying ? { scaleY: [0.2, BAR_CONFIGS[i * 5].scaleA, 0.2] } : { scaleY: 0.2 }}
                transition={isPlaying ? { duration: BAR_CONFIGS[i * 5].duration, repeat: Infinity, ease: "easeInOut", delay: i * 0.1 } : { duration: 0.3 }} />
            ))}
          </div>
        </button>

        {/* Play/Pause */}
        <motion.button
          onClick={togglePlay}
          whileTap={{ scale: 0.88 }}
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-base font-bold"
          style={{
            background: "var(--gradient-primary)",
            color: "hsl(var(--primary-foreground))",
            boxShadow: isPlaying ? "0 0 16px hsl(var(--primary) / 0.45)" : "none",
          }}
        >
          {isPlaying ? "⏸" : "▶"}
        </motion.button>

        {/* Close */}
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs"
          style={{ background: "hsl(var(--surface-2))", color: "hsl(var(--destructive))" }}
        >
          ✕
        </button>
      </div>
    </motion.div>
  );
}

// ─── Mobile Full-Screen Player ────────────────────────────────────────────────
function MobilePlayer({
  nowPlayingTrack, isPlaying, currentTime, duration, isLive,
  togglePlay, nextTrack, prevTrack, handleSeek, volume, setVolume,
  playlist, setPlaylist, trackIndex, setTrackIndex, activeSearchId, isShuffled, toggleShuffle,
  castState, castDevice, requestCast, stopCast,
  syncEnabled, setSyncEnabled, onClose, onMinimise, activeTab, setActiveTab,
  showSearch, setShowSearch, searchQuery, handleSearchInput, clearSearch,
  searchResults, isSearching, searchInputRef, addAfterCurrent, addToEnd,
  openCtx, setCtxMenu, playSearchResult,
  playAt, audioLoading,
  mobileToast,
}) {
  const TABS = [
    { id: "player", label: "Now Playing", icon: "🎵" },
    { id: "lyrics", label: "Lyrics", icon: "🎤" },
    { id: "queue", label: `Queue`, icon: "☰" },
  ];

  const btnBase = { background: "hsl(var(--surface-2))", color: "hsl(var(--muted-foreground))" };
  const thumbSrc = nowPlayingTrack?.thumbnail;
  const thumbMeta = nowPlayingTrack?._placeholderColor;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
      transition={{ type: "spring", stiffness: 280, damping: 26 }}
      className="fixed inset-0 flex flex-col"
      style={{ zIndex: 10001, background: "hsl(var(--surface-1) / 0.98)", backdropFilter: "blur(40px)", WebkitBackdropFilter: "blur(40px)", fontFamily: "'Space Grotesk', sans-serif" }}
    >
      <motion.div className="absolute inset-0 pointer-events-none"
        animate={{
          background: isPlaying
            ? ["radial-gradient(ellipse 80% 60% at 50% 110%, hsl(var(--primary) / 0.18) 0%, transparent 70%)", "radial-gradient(ellipse 80% 60% at 50% 110%, hsl(var(--primary) / 0.28) 0%, transparent 70%)", "radial-gradient(ellipse 80% 60% at 50% 110%, hsl(var(--primary) / 0.18) 0%, transparent 70%)"]
            : "radial-gradient(ellipse 80% 60% at 50% 110%, hsl(var(--primary) / 0.06) 0%, transparent 70%)"
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="flex items-center justify-between px-5 pt-safe pt-4 pb-3 shrink-0 relative"
        style={{ borderBottom: "1px solid hsl(var(--border) / 0.3)", zIndex: 1 }}>
        <div className="flex items-center gap-2.5">
          <motion.div animate={{ rotate: isPlaying ? 360 : 0 }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            className="w-5 h-5 rounded-full"
            style={{ background: "var(--gradient-primary)", boxShadow: isPlaying ? "0 0 10px hsl(var(--primary) / 0.5)" : "none" }} />
          <span className="text-xs font-bold tracking-widest uppercase" style={{ color: "hsl(var(--primary))" }}>🎵 Music Mode</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowSearch(s => !s)} className="w-8 h-8 rounded-full flex items-center justify-center text-xs"
            style={{ background: showSearch ? "hsl(var(--primary) / 0.15)" : "hsl(var(--surface-2))", color: showSearch ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))" }}>
            🔍
          </button>
          {/* ─── Minimise button ── */}
          <button
            onClick={onMinimise}
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs"
            style={{ background: "hsl(var(--surface-2))", color: "hsl(var(--muted-foreground))" }}
            title="Minimise player"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M5 12h14" />
            </svg>
          </button>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-xs"
            style={{ background: "hsl(var(--surface-2))", color: "hsl(var(--destructive))" }}>✕</button>
        </div>
      </div>

      <TabBar tabs={TABS} activeTab={activeTab} setActiveTab={setActiveTab} fullWidth={true} />

      <div className="flex-1 overflow-hidden relative">
        <AnimatePresence mode="wait">

          {activeTab === "player" && (
            <motion.div key="player" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="h-full flex flex-col px-6 py-12" style={{ justifyContent: "space-between" }}>
              <motion.div key={nowPlayingTrack?.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center gap-3 flex-1 mb-2">
                <div className="relative flex items-center justify-center" style={{ padding: 50 }}>
                  <div className="absolute overflow-hidden" style={{ width: 400, height: 260, zIndex: 0 }} aria-hidden="true">
                    {thumbSrc ? (
                      <img src={thumbSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", filter: "blur(20px) saturate(1.3)", transform: "scale(1..05)", opacity: 0.5 }} />
                    ) : thumbMeta ? (
                      <div style={{ width: "100%", height: "100%", background: `radial-gradient(circle at 60% 40%, ${thumbMeta.accent}88 0%, ${thumbMeta.bg} 70%)`, opacity: 0.6 }} />
                    ) : null}
                    <div style={{ position: "absolute", inset: 0, background: "hsl(var(--surface-1) / 0.35)", backdropFilter: "blur(2px)" }} />
                  </div>
                  <div className="relative" style={{ zIndex: 1 }}>
                    <Thumb src={thumbSrc} alt={nowPlayingTrack?.title} size={180} track={nowPlayingTrack} />
                    {isPlaying && (
                      <motion.div className="absolute inset-0 rounded-md"
                        animate={{ boxShadow: ["0 0 20px hsl(var(--primary) / 0.3)", "0 0 40px hsl(var(--primary) / 0.5)", "0 0 20px hsl(var(--primary) / 0.3)"] }}
                        transition={{ duration: 2, repeat: Infinity }} />
                    )}
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-xl" style={{ color: "hsl(var(--foreground))", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: "1.3" }}>{nowPlayingTrack?.title ?? "—"}</div>
                  <div className="text-sm mt-1" style={{ color: "hsl(var(--muted-foreground))" }}>{nowPlayingTrack?.artist ?? ""}</div>
                </div>
              </motion.div>

              <div className="flex flex-col gap-0">
                <div className="flex justify-center mb-3">
                  <Visualizer isPlaying={isPlaying} barCount={40} height={32} />
                </div>
                <Seekbar currentTime={currentTime} duration={duration} onSeek={handleSeek} isLive={isLive} height={5} />
                <div className="flex items-center justify-between mb-4" style={{ fontSize: 11, color: "hsl(var(--muted-foreground) / 0.6)", fontFamily: "monospace" }}>
                  {isLive ? (
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LIVE</span>
                  ) : <span>{fmtDuration(currentTime) ?? "0:00"}</span>}
                  {!isLive && <span>{fmtDuration(duration) ?? "—"}</span>}
                </div>

                <div className="flex items-center justify-between mb-4">
                  <button onClick={prevTrack} className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl" style={btnBase}>⏮</button>
                  <motion.button onClick={togglePlay} whileTap={{ scale: 0.92 }}
                    className="rounded-full flex items-center justify-center text-2xl font-bold"
                    style={{ width: 72, height: 72, background: "var(--gradient-primary)", color: "hsl(var(--primary-foreground))", boxShadow: isPlaying ? "0 0 30px hsl(var(--primary) / 0.55)" : "var(--shadow-md)" }}>
                    {audioLoading
                      ? <motion.span animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }} style={{ display: "block", width: 26, height: 26, border: "3px solid hsl(var(--primary-foreground) / 0.4)", borderTopColor: "hsl(var(--primary-foreground))", borderRadius: "50%" }} />
                      : isPlaying ? "⏸" : "▶"}
                  </motion.button>
                  <button onClick={nextTrack} className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl" style={btnBase}>⏭</button>
                  <CastButton castState={castState} castDevice={castDevice} onRequest={requestCast} onStop={stopCast} />
                </div>

                <div className="flex items-center gap-3 mb-2">
                  <span className="text-lg">{volume === 0 ? "🔇" : volume < 40 ? "🔈" : "🔊"}</span>
                  <input type="range" min={0} max={100} value={volume} onChange={(e) => setVolume(Number(e.target.value))}
                    className="flex-1 cursor-pointer" style={{ accentColor: "hsl(var(--primary))" }} />
                  <span className="text-xs font-mono w-8 text-right" style={{ color: "hsl(var(--muted-foreground))" }}>{volume}%</span>
                </div>

                <div className="flex justify-center items-center gap-1 pb-1" style={{ fontSize: 10, color: "hsl(var(--muted-foreground) / 0.35)" }}>
                  powered by <YoutubeIcon style={{ color: "#CD201F", width: 13, height: 13, marginLeft: 3 }} />
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "lyrics" && (
            <motion.div key="lyrics" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col">
              <div className="flex items-center gap-3 px-5 py-3 shrink-0" style={{ borderBottom: "1px solid hsl(var(--border) / 0.4)" }}>
                <Thumb src={nowPlayingTrack?.thumbnail} alt={nowPlayingTrack?.title} size={36} track={nowPlayingTrack} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{nowPlayingTrack?.title ?? "—"}</div>
                  <div className="text-xs opacity-55 truncate">{nowPlayingTrack?.artist ?? ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span style={{ fontSize: 10, color: "hsl(var(--muted-foreground) / 0.6)" }}>sync</span>
                  <button onClick={() => setSyncEnabled(s => !s)} className="relative"
                    style={{ width: 32, height: 18, borderRadius: 9, background: syncEnabled ? "hsl(var(--primary))" : "hsl(var(--surface-3))", border: "1px solid hsl(var(--border) / 0.5)", transition: "background 0.2s" }}>
                    <motion.div animate={{ x: syncEnabled ? 16 : 2 }} transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      style={{ position: "absolute", top: 2, width: 12, height: 12, borderRadius: "50%", background: syncEnabled ? "hsl(var(--primary-foreground))" : "hsl(var(--muted-foreground) / 0.5)" }} />
                  </button>
                </div>
                <button onClick={togglePlay} className="w-9 h-9 rounded-full flex items-center justify-center text-sm"
                  style={{ background: "var(--gradient-primary)", color: "hsl(var(--primary-foreground))" }}>
                  {isPlaying ? "⏸" : "▶"}
                </button>
              </div>
              <div className="px-5 py-2 shrink-0">
                <Seekbar currentTime={currentTime} duration={duration} onSeek={handleSeek} isLive={isLive} height={4} />
                <TimeDisplay currentTime={currentTime} duration={duration} isLive={isLive} />
              </div>
              <div className="flex-1 overflow-y-auto px-5" style={{ scrollbarWidth: "thin", scrollbarColor: "hsl(var(--primary) / 0.2) transparent" }}>
                <LyricsContent track={nowPlayingTrack} currentTime={currentTime} syncEnabled={syncEnabled} onSeek={handleSeek} mobile={true} />
              </div>
              <div className="flex justify-center items-center gap-1 py-3 shrink-0" style={{ fontSize: 10, color: "hsl(var(--muted-foreground) / 0.35)", borderTop: "1px solid hsl(var(--border) / 0.2)" }}>
                powered by syncedlyrics ✦
              </div>
            </motion.div>
          )}

          {activeTab === "queue" && (
            <motion.div key="queue" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col">
              <div className="flex items-center justify-between px-5 py-3 shrink-0" style={{ borderBottom: "1px solid hsl(var(--border) / 0.3)" }}>
                <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--muted-foreground) / 0.5)" }}>{playlist.length} tracks</span>
                <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground) / 0.3)" }}>tap · hold for menu · drag</span>
              </div>
              <Reorder.Group axis="y" values={playlist}
                onReorder={(newOrder) => {
                  const currentId = playlist[trackIndex]?.id;
                  setPlaylist(newOrder);
                  const ni = newOrder.findIndex((t) => t.id === currentId);
                  if (ni !== -1) setTrackIndex(ni);
                }}
                className="flex-1 overflow-y-auto px-3 py-2 space-y-1" style={{ scrollbarWidth: "thin" }}>
                {playlist.map((t, i) => {
                  const isActive = !activeSearchId && i === trackIndex;
                  return (
                    <QueueItem key={t.id} track={t} index={i} isActive={isActive} isPlaying={isPlaying}
                      isBase={BASE_IDS.has(t.id)} onPlay={() => playAt(i)} onContextMenu={(e) => openCtx(e, t, i, "queue")} />
                  );
                })}
              </Reorder.Group>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showSearch && (
            <MobileSearchPanel
              searchQuery={searchQuery} handleSearchInput={handleSearchInput} clearSearch={clearSearch}
              searchResults={searchResults} isSearching={isSearching} searchInputRef={searchInputRef}
              isPlaying={isPlaying} activeSearchId={activeSearchId}
              addAfterCurrent={addAfterCurrent} addToEnd={addToEnd} playSearchResult={playSearchResult}
              onClose={() => setShowSearch(false)} openCtx={openCtx}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Mobile toast rendered inside full-screen player */}
      <MobileToast message={mobileToast.msg} icon={mobileToast.icon} visible={mobileToast.visible} />
    </motion.div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MusicPlayer({ active, onClose }) {
  const isMobile = useIsMobile();
  const dragControls = useDragControls();

  const [playlist, setPlaylist] = useState(INITIAL_PLAYLIST);
  const [trackIndex, setTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const isPlayingRef = useRef(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMobileMinimised, setIsMobileMinimised] = useState(false);
  const [volume, setVolume] = useState(60);
  const [showVolume, setShowVolume] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const audioRef = useRef(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [isShuffled, setIsShuffled] = useState(true);
  const [nowPlayingTrack, setNowPlayingTrack] = useState(INITIAL_PLAYLIST[0]);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLive, setIsLive] = useState(false);
  const seekIntervalRef = useRef(null);

  const [activeTab, setActiveTab] = useState("player");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [activeSearchId, setActiveSearchId] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [toast, setToast] = useState({ msg: "", icon: "", visible: false });
  const [mobileToast, setMobileToast] = useState({ msg: "", icon: "", visible: false });
  const toastTimerRef = useRef(null);
  const mobileToastTimerRef = useRef(null);
  const dragConstraintsRef = useRef(null);

  const playerRef = useRef(null);
  const playerReadyRef = useRef(false);
  const loadedVideoIdRef = useRef(null);
  const isLiveRef = useRef(false);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const erroredIdsRef = useRef(new Set());
  const pillDraggedRef = useRef(false);

  const [activeVideoId, setActiveVideoId] = useState(INITIAL_PLAYLIST[0].id);
  const [syncEnabled, setSyncEnabled] = useState(false);

  const playlistRef = useRef(playlist);
  const trackIndexRef = useRef(trackIndex);
  useEffect(() => { playlistRef.current = playlist; }, [playlist]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { trackIndexRef.current = trackIndex; }, [trackIndex]);

  useEffect(() => { playerReadyRef.current = playerReady; }, [playerReady]);
  useEffect(() => { isLiveRef.current = isLive; }, [isLive]);

  const showToast = useCallback((msg, icon = "") => {
    setToast({ msg, icon, visible: true });
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast((t) => ({ ...t, visible: false })), 2400);
  }, []);

  const showMobileToast = useCallback((msg, icon = "") => {
    setMobileToast({ msg, icon, visible: true });
    clearTimeout(mobileToastTimerRef.current);
    mobileToastTimerRef.current = setTimeout(() => setMobileToast((t) => ({ ...t, visible: false })), 2400);
  }, []);

  const showAnyToast = useCallback((msg, icon = "") => {
    if (isMobile) showMobileToast(msg, icon);
    else showToast(msg, icon);
  }, [isMobile, showMobileToast, showToast]);

  const stopPolling = useCallback(() => clearInterval(seekIntervalRef.current), []);

  const handleSeek = useCallback((seconds) => {
    if (isLiveRef.current) return;
    if (usingAudioFallbackRef.current && audioRef.current) {
    audioRef.current.currentTime = seconds;
    setCurrentTime(seconds);
    return;
  }
    if (FORCE_IFRAME && playerRef.current) {
      // IFrame mode: seek via the YT player directly
      playerRef.current.seekTo(seconds, true);
      setCurrentTime(seconds);
    } else if (audioRef.current && audioRef.current.src && audioRef.current.src.includes("/api/audio-proxy")) {
      audioRef.current.currentTime = seconds;
      setCurrentTime(seconds);
    } else if (playerRef.current) {
      playerRef.current.seekTo(seconds, true);
      setCurrentTime(seconds);
    }
  }, []);

  const addAfterCurrent = useCallback((t) => {
    if (!t?.id) return;
    setPlaylist((prev) => {
      const next = [...prev];
      next.splice(trackIndexRef.current + 1, 0, { ...t });
      return next;
    });
    showAnyToast(`Playing next: ${t.title.slice(0, 24)}…`, "⏭");
  }, [showAnyToast]);

  const addToEnd = useCallback((t) => {
    if (!t?.id) return;
    setPlaylist((prev) => [...prev, { ...t }]);
    showAnyToast("Added to queue", "＋");
  }, [showAnyToast]);

  const removeFromQueue = useCallback((idx) => {
    // if (BASE_IDS.has(playlist[idx]?.id)) return;
    setPlaylist((prev) => { const n = [...prev]; n.splice(idx, 1); return n; });
    setTrackIndex((i) => idx < i ? i - 1 : idx === i ? Math.min(idx, playlist.length - 2) : i);
    showAnyToast("Removed from queue", "✕");
  }, [playlist, showAnyToast]);

  const moveToPlayNext = useCallback((fromIdx) => {
    const currentId = playlist[trackIndex]?.id;
    if (fromIdx === trackIndex || fromIdx === trackIndex + 1) return;
    setPlaylist((prev) => {
      const next = [...prev];
      const [item] = next.splice(fromIdx, 1);
      const ci = next.findIndex((t) => t.id === currentId);
      next.splice(ci + 1, 0, item);
      return next;
    });
    showAnyToast("Playing next ↑", "⏭");
  }, [playlist, trackIndex, showAnyToast]);

  const openInYoutube = useCallback((id) => { window.open(`https://www.youtube.com/watch?v=${id}`, "_blank", "noopener,noreferrer"); }, []);

  const copyUrl = useCallback(async (id) => {
    const url = `https://youtu.be/${id}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = url;
        textArea.style.position = "fixed";
        textArea.style.opacity = 0;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      showAnyToast("URL copied!", "🔗");
    } catch (err) {
      console.warn("Copy failed:", err);
      showAnyToast("Copy failed", "⚠️");
    }
  }, [showAnyToast]);

  const openCtx = useCallback((e, t, idx, source) => {
    e.preventDefault(); e.stopPropagation();
    setCtxMenu({ x: e.clientX, y: e.clientY, track: t, index: idx, source });
  }, []);

  const loadYTLive = useCallback((videoId, shouldAutoplay = false) => {
    const attempt = () => {
      if (playerRef.current && playerReadyRef.current) {
        if (shouldAutoplay) {
        playerRef.current.loadVideoById(videoId);
      } else {
        playerRef.current.cueVideoById(videoId);
      }
        return true;
      }
      return false;
    };
    if (attempt()) return;
    const interval = setInterval(() => {
      if (attempt()) clearInterval(interval);
    }, 200);
    setTimeout(() => clearInterval(interval), 10000);
  }, []);

  const usingAudioFallbackRef = useRef(false);

  const loadYTIframe = useCallback((videoId, shouldAutoplay = false,  onError = null) => {
    const attempt = () => {
      if (playerRef.current && playerReadyRef.current) {
        setIsLive(false);
        iframeErrorFallbackRef.current = onError;
        if (shouldAutoplay) {
        playerRef.current.loadVideoById(videoId);
      } else {
        playerRef.current.cueVideoById(videoId);
      }
        return true;
      }
      return false;
    };
    if (attempt()) return;
    const interval = setInterval(() => {
      if (attempt()) clearInterval(interval);
    }, 200);
    setTimeout(() => clearInterval(interval), 10000);
  }, []);

  const [pollEpoch, setPollEpoch] = useState(0);

  const loadAudioTrack = useCallback(async (videoId, autoplay = false) => {
    if (!audioRef.current) return;
    if (loadedVideoIdRef.current === videoId) return;
    loadedVideoIdRef.current = videoId;
    lastLoadedIdRef.current = videoId;
    usingAudioFallbackRef.current = false;
    setPollEpoch(e => e + 1);
    setAudioLoading(true);
    setCurrentTime(0); setDuration(0); setIsLive(false);
    setIsPlaying(false);

    // Stop any active polling from previous track
    clearInterval(seekIntervalRef.current);

    // Stop IFrame player if it was playing previous track
    if (playerRef.current) {
      try { playerRef.current.stopVideo(); } catch (_) {}
    } 

    const clearAudio = () => {
      if (!audioRef.current) return;
      audioRef.current.pause();
      audioRef.current.loop = false;
      audioRef.current.removeAttribute("src");
      audioRef.current.load();
    };

    // ─── FORCE_IFRAME bypass ──────────────────────────────────────────────
    // Skip the audio-proxy but still call audio-url to detect is_live so
    // the LIVE badge / seekbar state is shown correctly.
    if (FORCE_IFRAME) {
       clearAudio();
      if (audioRef.current) audioRef.current.pause();
      try {
        const res = await fetch(
          `https://portfolio-backend-291262241596.us-central1.run.app/api/track-info?video_id=${encodeURIComponent(videoId)}`,
          { method: "POST" }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.is_live) {
            setIsLive(true);
            setAudioLoading(false);
            // Start silent keepalive so the OS doesn't suspend on lock screen
            // if (audioRef.current) {
            //   audioRef.current.src = SILENT_AUDIO_SRC;
            //   audioRef.current.loop = true;
            //   audioRef.current.volume = 0.001;
            //   audioRef.current.play().catch(() => {});
            // }
            loadYTLive(videoId, autoplay);   // live polling handled by seekIntervalRef
            return;
          }
        }
      } catch (_) {}
      // Not live (or API unreachable) — load normally via IFrame
      setIsLive(false);
      setAudioLoading(false);
      // Start silent keepalive so the OS doesn't suspend on lock screen
      // if (audioRef.current) {
      //   audioRef.current.src = SILENT_AUDIO_SRC;
      //   audioRef.current.loop = true;
      //   audioRef.current.volume = 0.001;
      //   audioRef.current.play().catch(() => {});
      // }
      loadYTIframe(videoId, autoplay, () => {
  // IFrame failed — fall back to audio proxy
  if (!audioRef.current) return;
  usingAudioFallbackRef.current = true;
  audioRef.current.src = `https://portfolio-backend-291262241596.us-central1.run.app/api/audio-proxy?video_id=${encodeURIComponent(videoId)}`;
  audioRef.current.loop = false;
  audioRef.current.volume = 1;
  audioRef.current.load();
  if (autoplay || isPlayingRef.current) {
    audioRef.current.play().catch(() => {});
  }
});
      return;
    }
    // ─────────────────────────────────────────────────────────────────────

    try {
      const res = await fetch(`https://portfolio-backend-291262241596.us-central1.run.app/api/track-info?video_id=${encodeURIComponent(videoId)}`, { method: "POST" });
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();

      if (data.is_live) {
        setAudioLoading(false);
        setIsLive(true);
        if (audioRef.current) { audioRef.current.pause(); }
        loadYTLive(videoId);
        return;
      }

      if (playerRef.current) {
        try { playerRef.current.stopVideo(); } catch (_) {}
      }

      audioRef.current.src = `https://portfolio-backend-291262241596.us-central1.run.app/api/audio-proxy?video_id=${encodeURIComponent(videoId)}`;
      audioRef.current.load();
      // Only auto-play if already playing (e.g. user clicked next/prev mid-playback)
      if (isPlayingRef.current) {
        const playPromise = audioRef.current.play();
        if (playPromise) playPromise.catch(() => {});
      }
    } catch (err) {
      console.warn("Audio URL fetch failed, falling back to YT IFrame:", err);
      setAudioLoading(false);
      loadYTIframe(videoId);
    }
  }, [loadYTLive, loadYTIframe]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // In FORCE_IFRAME mode the <audio> element only plays a silent keepalive
    // loop — all playback events (ended, error, timeupdate, etc.) must be
    // ignored so they don't interfere with IFrame-driven state.
    const onPlay = () => { if (FORCE_IFRAME && !usingAudioFallbackRef.current) return; setIsPlaying(true); setAudioLoading(false); };
    const onPause = () => { if (FORCE_IFRAME && !usingAudioFallbackRef.current) return; setIsPlaying(false); };
    const onTimeUpdate = () => { if (FORCE_IFRAME && !usingAudioFallbackRef.current) return; setCurrentTime(audio.currentTime); };
    const onDurationChange = () => {
      if (FORCE_IFRAME && !usingAudioFallbackRef.current) return;
      const d = audio.duration;
      setDuration(isFinite(d) ? d : 0);
    };
    const onEnded = () => {
      if (FORCE_IFRAME && !usingAudioFallbackRef.current) return; // silent loop ending — not a real track ending
      const pl = playlistRef.current;
      const idx = trackIndexRef.current;
      const nextIdx = (idx + 1) % pl.length;
      setTrackIndex(nextIdx);
      setNowPlayingTrack(pl[nextIdx]);
      setActiveVideoId(pl[nextIdx].id);
    };
    const onError = () => {
      if (FORCE_IFRAME && !usingAudioFallbackRef.current) return; // silent audio errors are non-fatal
      console.warn("Audio element error, skipping track");
      const currentId = playlistRef.current[trackIndexRef.current]?.id;
      if (currentId) erroredIdsRef.current.add(currentId);
      onEnded();
    };
    const onWaiting = () => { if (FORCE_IFRAME && !usingAudioFallbackRef.current) return; setAudioLoading(true); };
    const onCanPlay = () => { if (FORCE_IFRAME && !usingAudioFallbackRef.current) return; setAudioLoading(false); };

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("canplay", onCanPlay);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("canplay", onCanPlay);
    };
  }, []);

  const iframeErrorFallbackRef = useRef(null);
  useEffect(() => {
    const initPlayer = () => {
      if (playerRef.current) return;
      playerRef.current = new window.YT.Player("yt-music-hidden", {
        height: "1", width: "1",
        videoId: INITIAL_PLAYLIST[0].id,
        playerVars: { autoplay: 0, controls: 0, disablekb: 1, fs: 0, iv_load_policy: 3, modestbranding: 1, rel: 0 },
        events: {
          onReady: () => {
            setPlayerReady(true);
            playerReadyRef.current = true;
          },
          onStateChange: (e) => {
            // Skip IFrame events only when the audio-proxy is actively driving
            // playback AND we are NOT in FORCE_IFRAME or live mode.
            if (!FORCE_IFRAME && !isLiveRef.current && audioRef.current?.src?.includes("/api/audio-proxy")) return;

             // Clear stale duration while new track is buffering
            if (e.data === window.YT.PlayerState.BUFFERING) {
              setDuration(0);
              setCurrentTime(0);
            }

            const playing = e.data === window.YT.PlayerState.PLAYING;
            setIsPlaying(playing);
            if (playing) {
              try {
                iframeErrorFallbackRef.current = null;
                const dur = e.target.getDuration() || 0;
                setDuration(dur);
              } catch (_) {}
            }
            if (e.data === window.YT.PlayerState.ENDED) {
              const pl = playlistRef.current;
              const idx = trackIndexRef.current;
              const next = (idx + 1) % pl.length;
              setTrackIndex(next); setNowPlayingTrack(pl[next]); setActiveVideoId(pl[next].id); loadAudioTrack(pl[next].id, true); 
            }
          },
          onError: (e) => {
            console.warn("YT IFrame error:", e.data);

            if (iframeErrorFallbackRef.current) {
    iframeErrorFallbackRef.current();
    iframeErrorFallbackRef.current = null;
    return;
  }
  
            const pl = playlistRef.current;
            const idx = trackIndexRef.current;
            const next = (idx + 1) % pl.length;
            setTrackIndex(next); setNowPlayingTrack(pl[next]); setActiveVideoId(pl[next].id);
          },
        },
      });
    };
    if (window.YT?.Player) { initPlayer(); return; }
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    }
    window.onYouTubeIframeAPIReady = initPlayer;
    return () => { window.onYouTubeIframeAPIReady = null; };
  }, []);

  const lastLoadedIdRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  useEffect(() => {
    if (!activeVideoId) return;
    if (activeVideoId === lastLoadedIdRef.current) return; // already loaded by onStateChange
  lastLoadedIdRef.current = activeVideoId;
  if (isInitialLoadRef.current) {
    isInitialLoadRef.current = false;
    loadAudioTrack(activeVideoId, false); // first load → no autoplay
  } else {
    loadAudioTrack(activeVideoId, true);  // any user action → always autoplay
  }
  }, [activeVideoId]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume / 100;
    if (playerReady && playerRef.current) {
      try { playerRef.current.setVolume(volume); } catch (_) {}
    }
  }, [volume, playerReady]);

  useEffect(() => {
    // Poll the IFrame player for currentTime + duration whenever we are in
    // live mode OR FORCE_IFRAME mode (the <audio> element won't fire events).
    const shouldPoll = isLive || FORCE_IFRAME;
    if (!shouldPoll) { clearInterval(seekIntervalRef.current); return; }
    seekIntervalRef.current = setInterval(() => {
      if (usingAudioFallbackRef.current) return;
      try {
        const ct = playerRef.current?.getCurrentTime() || 0;
        setCurrentTime(ct);
        if (FORCE_IFRAME && !isLive) {
          const dur = playerRef.current?.getDuration() || 0;
          const state = playerRef.current?.getPlayerState?.();
        if (dur > 0 && state === window.YT?.PlayerState?.PLAYING) {
          setDuration(dur);
        }
        }
      } catch (_) {}
    }, 250);
    return () => clearInterval(seekIntervalRef.current);
  }, [isLive, pollEpoch]);

  const togglePlay = () => {
    if (usingAudioFallbackRef.current && audioRef.current) {
    // Audio fallback is active — ignore IFrame entirely
    isPlaying ? audioRef.current.pause() : audioRef.current.play().catch(() => {});
    return;
  }
    if (isLive && playerRef.current) {
      if (isPlaying) {
        playerRef.current.pauseVideo();
        // Pause silent keepalive too so the OS shows a paused state
        if (FORCE_IFRAME) audioRef.current?.pause();
      } else {
        playerRef.current.playVideo();
        if (FORCE_IFRAME) audioRef.current?.play().catch(() => {});
      }
    } else if (FORCE_IFRAME && playerRef.current) {
      // Play/pause both IFrame and the silent keepalive together
      if (isPlaying) {
        playerRef.current.pauseVideo();
        audioRef.current?.pause();
      } else {
        playerRef.current.playVideo();
        audioRef.current?.play().catch(() => {});
      }
    } else if (audioRef.current) {
      isPlaying ? audioRef.current.pause() : audioRef.current.play().catch(() => {});
    }
  };

  const nextTrack = useCallback(() => {
    setActiveSearchId(null);
    const pl = playlistRef.current;
    const idx = trackIndexRef.current;
    const nextIdx = (idx + 1) % pl.length;
    setTrackIndex(nextIdx);
    setNowPlayingTrack(pl[nextIdx]);
    setActiveVideoId(pl[nextIdx].id);
  }, []);

  const prevTrack = useCallback(() => {
    setActiveSearchId(null);
    const pl = playlistRef.current;
    const idx = trackIndexRef.current;
    const prevIdx = (idx - 1 + pl.length) % pl.length;
    setTrackIndex(prevIdx);
    setNowPlayingTrack(pl[prevIdx]);
    setActiveVideoId(pl[prevIdx].id);
  }, []);

  const onMediaPlay = useCallback(() => {
    if (usingAudioFallbackRef.current) { audioRef.current?.play().catch(() => {}); return; }
    if (isLiveRef.current && playerRef.current) {
      playerRef.current.playVideo();
      if (FORCE_IFRAME) audioRef.current?.play().catch(() => {});
    } else if (FORCE_IFRAME && playerRef.current) {
      playerRef.current.playVideo();
      audioRef.current?.play().catch(() => {});
    } else {
      audioRef.current?.play().catch(() => {});
    }
  }, []);

  const onMediaPause = useCallback(() => {
    if (usingAudioFallbackRef.current) { audioRef.current?.pause(); return; }
    if (isLiveRef.current && playerRef.current) {
      playerRef.current.pauseVideo();
      if (FORCE_IFRAME) audioRef.current?.pause();
    } else if (FORCE_IFRAME && playerRef.current) {
      playerRef.current.pauseVideo();
      audioRef.current?.pause();
    } else {
      audioRef.current?.pause();
    }
  }, []);

  useMediaSession({ track: nowPlayingTrack, isPlaying, onPlay: onMediaPlay, onPause: onMediaPause, onNext: nextTrack, onPrev: prevTrack });

  const { castState, castDevice, requestCast, stopCast } = useCast({ track: nowPlayingTrack, isPlaying });

  const playAt = (i) => {
    setActiveSearchId(null);
    setTrackIndex(i);
    setNowPlayingTrack(playlist[i]);
    setActiveVideoId(playlist[i].id);
  };

  const playSearchResult = useCallback((id) => {
    const result = searchResults.find((r) => r.id === id);
    if (!result) return;
    setActiveSearchId(id);
    setNowPlayingTrack(result);
    setActiveVideoId(id);
    loadAudioTrack(id, true); 
  }, [searchResults]);

  const toggleShuffle = () => {
    const current = playlist[trackIndex];
    const newPl = isShuffled ? [...BASE_PLAYLIST_WITH_META] : shuffleArray(BASE_PLAYLIST_WITH_META);
    const newIdx = newPl.findIndex((t) => t.id === current.id);
    setPlaylist(newPl);
    setTrackIndex(newIdx === -1 ? 0 : newIdx);
    setIsShuffled((s) => !s);
  };

  const searchYouTube = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      const res = await fetch(`https://portfolio-backend-291262241596.us-central1.run.app/api/youtube/search?q=${encodeURIComponent(q)}&limit=20`, { method: "POST" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSearchResults(data.map((v) => ({ id: v.id, title: v.title, artist: v.artist, duration: v.duration, thumbnail: v.thumbnail })));
    } catch {
      setSearchResults([{ id: null, title: "Search failed", artist: "Check API server is running" }]);
    }
    setIsSearching(false);
  }, []);

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(searchTimeoutRef.current);
    if (!val.trim()) { setSearchResults([]); return; }
    searchTimeoutRef.current = setTimeout(() => searchYouTube(val), 500);
  };

  const clearSearch = () => { setSearchQuery(""); setSearchResults([]); };

  useEffect(() => {
    if (showSearch) setTimeout(() => searchInputRef.current?.focus(), 150);
  }, [showSearch]);

  const handleClose = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      try { playerRef.current?.pauseVideo(); } catch (_) {}
    }
    onClose();
  };

  const cardStyle = {
    background: "hsl(var(--surface-1) / 0.96)",
    border: "1px solid hsl(var(--primary) / 0.2)",
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
    boxShadow: "var(--shadow-lg)",
  };
  const btnBase = { background: "hsl(var(--surface-2))", color: "hsl(var(--muted-foreground))" };

  const DESKTOP_TABS = [
    { id: "player", label: "Now Playing", icon: "🎵" },
    { id: "lyrics", label: "Lyrics", icon: "🎤" },
    { id: "queue",  label: `Queue (${playlist.length})`, icon: "☰" },
  ];

  const sharedMobileProps = {
    nowPlayingTrack, isPlaying, currentTime, duration, isLive,
    togglePlay, nextTrack, prevTrack, handleSeek, volume, setVolume,
    playlist, setPlaylist, trackIndex, setTrackIndex, activeSearchId, isShuffled, toggleShuffle,
    castState, castDevice, requestCast, stopCast,
    syncEnabled, setSyncEnabled, onClose: handleClose,
    onMinimise: () => setIsMobileMinimised(true),
    activeTab, setActiveTab,
    showSearch, setShowSearch, searchQuery, handleSearchInput, clearSearch,
    searchResults, isSearching, searchInputRef,
    addAfterCurrent, addToEnd,
    openCtx, setCtxMenu, playSearchResult, playAt,
    audioLoading,
    mobileToast,
  };

  return (
    <>
      <audio ref={audioRef} style={{ display: "none" }} preload="auto" />
      <div id="yt-music-hidden" style={{ position: "fixed", bottom: 0, right: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none", zIndex: -1 }} />

      <AnimatePresence>
        {ctxMenu && (
          <ContextMenu
            x={ctxMenu.x} y={ctxMenu.y} track={ctxMenu.track}
            isBase={BASE_IDS.has(ctxMenu.track?.id)}
            isActive={!activeSearchId && playlist[trackIndex]?.id === ctxMenu.track?.id}
            isPlaying={isPlaying} source={ctxMenu.source}
            onClose={() => setCtxMenu(null)}
            onPlayNow={() => ctxMenu.source === "search" ? playSearchResult(ctxMenu.track.id) : playAt(ctxMenu.index)}
            onPlayNext={() => ctxMenu.source === "search" ? addAfterCurrent(ctxMenu.track) : moveToPlayNext(ctxMenu.index)}
            onAddToQueue={() => ctxMenu.source === "search" ? addToEnd(ctxMenu.track) : showAnyToast("Already in queue", "✓")}
            onRemove={() => removeFromQueue(ctxMenu.index)}
            onOpenYoutube={() => openInYoutube(ctxMenu.track?.id)}
            onCopyUrl={() => copyUrl(ctxMenu.track?.id)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {active && isMobile && !isMobileMinimised && (
          <MobilePlayer key="mobile-full" {...sharedMobileProps} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {active && isMobile && isMobileMinimised && (
          <MobileMiniPlayer
            key="mobile-mini"
            nowPlayingTrack={nowPlayingTrack}
            isPlaying={isPlaying}
            togglePlay={togglePlay}
            onExpand={() => setIsMobileMinimised(false)}
            onClose={handleClose}
          />
        )}
      </AnimatePresence>

      {active && isMobile && isMobileMinimised && (
        <MobileToast message={mobileToast.msg} icon={mobileToast.icon} visible={mobileToast.visible} />
      )}

      {!isMobile && (
        <>
          <div ref={dragConstraintsRef} className="fixed inset-0 pointer-events-none" style={{ zIndex: 10000 }} />

          <motion.div
            drag dragControls={dragControls} dragListener={false} dragMomentum={false} dragElastic={0}
            dragConstraints={dragConstraintsRef}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={!active ? { opacity: 0, scale: 0.85, pointerEvents: "none" } : { opacity: 1, scale: 1, pointerEvents: "auto" }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="fixed bottom-6 left-6 select-none"
            style={{ zIndex: 10001, fontFamily: "'Space Grotesk', sans-serif" }}
          >
            <motion.div className="absolute inset-0 rounded-2xl pointer-events-none"
              animate={{ boxShadow: isPlaying ? ["0 0 30px hsl(var(--glow-primary)), 0 0 60px hsl(var(--glow-secondary))", "0 0 50px hsl(var(--primary) / 0.3), 0 0 100px hsl(var(--glow-secondary))", "0 0 30px hsl(var(--glow-primary)), 0 0 60px hsl(var(--glow-secondary))"] : "0 0 0px transparent" }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            />

            <FloatingToast message={toast.msg} icon={toast.icon} visible={toast.visible} />

            <AnimatePresence mode="wait">
              {isExpanded ? (
                <motion.div key="expanded"
                  initial={{ opacity: 0, y: 10, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.97 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}>
                  <div className="rounded-2xl w-72 overflow-hidden" style={cardStyle}>

                    <div className="flex items-center justify-between px-4 pt-4 pb-3" style={{ cursor: "grab" }}
                      onPointerDown={(e) => { if (e.target.closest("button")) return; dragControls.start(e); }}>
                      <div className="flex items-center gap-2">
                        <motion.div animate={{ rotate: isPlaying ? 360 : 0 }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                          className="w-5 h-5 rounded-full shrink-0"
                          style={{ background: "var(--gradient-primary)", boxShadow: "0 0 8px hsl(var(--primary) / 0.5)" }} />
                        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "hsl(var(--primary))" }}>🎵 Music Mode</span>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setShowSearch((s) => !s)}
                          className="w-6 h-6 rounded-full flex items-center justify-center text-xs transition-all hover:scale-110"
                          style={{ background: showSearch ? "hsl(var(--primary) / 0.15)" : "hsl(var(--surface-2))", color: showSearch ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))", border: showSearch ? "1px solid hsl(var(--primary) / 0.3)" : "1px solid transparent" }}>
                          🔍
                        </button>
                        <button onClick={() => setIsExpanded(false)} className="w-6 h-6 rounded-full flex items-center justify-center text-xs transition-all hover:scale-110" style={btnBase}>—</button>
                        <button onClick={handleClose} className="w-6 h-6 rounded-full flex items-center justify-center text-xs transition-all hover:scale-110" style={{ background: "hsl(var(--surface-2))", color: "hsl(var(--destructive))" }}>✕</button>
                      </div>
                    </div>

                    <AnimatePresence>
                      {showSearch && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden px-4">
                          <div className="flex items-center gap-2 rounded-xl px-3 py-2 mb-2" style={{ background: "hsl(var(--surface-2))", border: "1px solid hsl(var(--primary) / 0.15)" }}>
                            <span className="text-xs" style={{ color: "hsl(var(--muted-foreground))" }}>🔍</span>
                            <input ref={searchInputRef} type="text" value={searchQuery} onChange={handleSearchInput}
                              placeholder="Search YouTube…" className="flex-1 bg-transparent text-xs outline-none"
                              style={{ color: "hsl(var(--foreground))", caretColor: "hsl(var(--primary))" }}
                              onKeyDown={(e) => e.stopPropagation()} />
                            {searchQuery && <button onClick={clearSearch} className="text-xs opacity-50 hover:opacity-100" style={{ color: "hsl(var(--muted-foreground))" }}>✕</button>}
                          </div>
                          {isSearching && (
                            <div className="flex justify-center py-3">
                              <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                                className="w-4 h-4 rounded-full"
                                style={{ border: "2px solid hsl(var(--primary) / 0.3)", borderTopColor: "hsl(var(--primary))" }} />
                            </div>
                          )}
                          {!isSearching && searchResults.length > 0 && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                              className="space-y-1 max-h-48 overflow-y-auto mb-2"
                              style={{ scrollbarWidth: "thin", scrollbarColor: "hsl(var(--primary) / 0.3) transparent" }}>
                              {searchResults.map((r, i) => {
                                const isActivePlaying = activeSearchId === r.id;
                                return (
                                  <div key={r.id ?? i} onContextMenu={(e) => r.id && openCtx(e, r, i, "search")}>
                                    <SearchResultRow r={r} isActivePlaying={isActivePlaying} isPlaying={isPlaying}
                                      onPlay={() => r.id && playSearchResult(r.id)}
                                      onAddNext={() => addAfterCurrent(r)} onAddEnd={() => addToEnd(r)}
                                      size={38} compact={true} />
                                  </div>
                                );
                              })}
                            </motion.div>
                          )}
                          <div className="pb-3 text-center text-[9px]" style={{ color: "hsl(var(--muted-foreground) / 0.3)", borderBottom: "1px solid hsl(var(--border))" }}>
                            right-click for more options
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <TabBar tabs={DESKTOP_TABS} activeTab={activeTab} setActiveTab={setActiveTab}
                      borderStyle={{ paddingTop: 12, paddingLeft: 16, paddingRight: 16 }} />

                    <div className="px-4 pb-4">
                      <AnimatePresence mode="wait">

                        {activeTab === "player" && (
                          <motion.div key="player" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                            <div className="pt-3 mb-3 px-1"><Visualizer isPlaying={isPlaying} /></div>
                            <motion.div key={nowPlayingTrack?.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="mb-3 flex items-center gap-3">
                              <Thumb src={nowPlayingTrack?.thumbnail} alt={nowPlayingTrack?.title} size={38} track={nowPlayingTrack} />
                              <div className="flex flex-col min-w-0">
                                <div className="font-semibold text-sm truncate" style={{ color: "hsl(var(--foreground))" }}>{nowPlayingTrack?.title ?? "—"}</div>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="text-xs truncate" style={{ color: "hsl(var(--muted-foreground))" }}>{nowPlayingTrack?.artist ?? ""}</span>
                                  {activeSearchId && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium"
                                      style={{ background: "hsl(var(--secondary) / 0.15)", color: "hsl(var(--secondary))", border: "1px solid hsl(var(--secondary) / 0.25)" }}>
                                      search
                                    </span>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                            <Seekbar currentTime={currentTime} duration={duration} onSeek={handleSeek} isLive={isLive} />
                            <TimeDisplay currentTime={currentTime} duration={duration} isLive={isLive} />
                            <div className="flex items-center justify-between">
                              <button onClick={prevTrack} className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-110 active:scale-95" style={btnBase}>⏮</button>
                              <motion.button onClick={togglePlay} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.92 }}
                                className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
                                style={{ background: "var(--gradient-primary)", color: "hsl(var(--primary-foreground))", boxShadow: isPlaying ? "0 0 24px hsl(var(--primary) / 0.5)" : "var(--shadow-md)" }}>
                                {audioLoading
                                  ? <motion.span animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }} style={{ display: "block", width: 18, height: 18, border: "2px solid hsl(var(--primary-foreground) / 0.4)", borderTopColor: "hsl(var(--primary-foreground))", borderRadius: "50%" }} />
                                  : isPlaying ? "⏸" : "▶"}
                              </motion.button>
                              <button onClick={nextTrack} className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-110 active:scale-95" style={btnBase}>⏭</button>
                              <button onClick={toggleShuffle} className="w-9 h-9 rounded-xl flex items-center justify-center text-xs transition-all hover:scale-110"
                                style={{ background: isShuffled ? "hsl(var(--primary) / 0.12)" : "hsl(var(--surface-2))", color: isShuffled ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))", border: isShuffled ? "1px solid hsl(var(--primary) / 0.25)" : "1px solid transparent" }}>
                                🔀
                              </button>
                              <CastButton castState={castState} castDevice={castDevice} onRequest={requestCast} onStop={stopCast} />
                              <div className="relative">
                                <button onClick={() => setShowVolume((v) => !v)} className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:scale-110 text-sm"
                                  style={{ background: showVolume ? "hsl(var(--primary) / 0.12)" : "hsl(var(--surface-2))", color: showVolume ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))", border: showVolume ? "1px solid hsl(var(--primary) / 0.25)" : "1px solid transparent" }}>
                                  {volume === 0 ? "🔇" : volume < 40 ? "🔈" : "🔊"}
                                </button>
                                <AnimatePresence>
                                  {showVolume && (
                                    <motion.div initial={{ opacity: 0, y: 6, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 6, scale: 0.95 }}
                                      className="absolute bottom-12 right-0 rounded-xl p-3 flex flex-col items-center gap-2 z-10"
                                      style={{ background: "hsl(var(--surface-1) / 0.98)", border: "1px solid hsl(var(--primary) / 0.2)", backdropFilter: "blur(20px)", boxShadow: "var(--shadow-md)" }}>
                                      <span className="text-xs font-mono" style={{ color: "hsl(var(--primary))" }}>{volume}%</span>
                                      <input type="range" min={0} max={100} value={volume} onChange={(e) => setVolume(Number(e.target.value))}
                                        className="h-20 cursor-pointer" style={{ writingMode: "vertical-lr", direction: "rtl", accentColor: "hsl(var(--primary))" }} />
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            </div>
                            <div className="flex mt-4 items-center justify-center text-xs" style={{ color: "hsl(var(--muted-foreground) / 0.5)" }}>
                              powered by &nbsp;<YoutubeIcon style={{ color: "#CD201F" }} />
                            </div>
                            <div className="mt-2 text-center text-xs" style={{ color: "hsl(var(--muted-foreground) / 0.5)" }}>
                              secret unlocked ✦ type <span style={{ color: "hsl(var(--primary) / 0.6)", fontFamily: "monospace" }}>game</span> for more
                            </div>
                          </motion.div>
                        )}

                        {activeTab === "lyrics" && (
                          <motion.div key="lyrics" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                            <div className="flex items-center gap-2 py-2.5 mb-2" style={{ borderBottom: "1px solid hsl(var(--border) / 0.5)" }}>
                              <Thumb src={nowPlayingTrack?.thumbnail} alt={nowPlayingTrack?.title} size={28} track={nowPlayingTrack} />
                              <div className="min-w-0 flex-1">
                                <div className="text-[11px] font-semibold truncate" style={{ color: "hsl(var(--foreground))" }}>{nowPlayingTrack?.title ?? "—"}</div>
                                <div className="text-[10px] opacity-55 truncate" style={{ color: "hsl(var(--muted-foreground))" }}>{nowPlayingTrack?.artist ?? ""}</div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-[9px] font-medium" style={{ color: "hsl(var(--muted-foreground) / 0.6)" }}>sync</span>
                                <button onClick={() => setSyncEnabled(s => !s)} className="relative shrink-0 transition-all"
                                  style={{ width: 26, height: 14, borderRadius: 7, background: syncEnabled ? "hsl(var(--primary))" : "hsl(var(--surface-3))", border: "1px solid hsl(var(--border) / 0.5)", transition: "background 0.2s" }}>
                                  <motion.div animate={{ x: syncEnabled ? 13 : 1 }} transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                    style={{ position: "absolute", top: 1, width: 10, height: 10, borderRadius: "50%", background: syncEnabled ? "hsl(var(--primary-foreground))" : "hsl(var(--muted-foreground) / 0.5)" }} />
                                </button>
                              </div>
                              <button onClick={togglePlay} className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs"
                                style={{ background: "var(--gradient-primary)", color: "hsl(var(--primary-foreground))" }}>
                                {isPlaying ? "⏸" : "▶"}
                              </button>
                            </div>
                            <div className="mb-3">
                              <Seekbar currentTime={currentTime} duration={duration} onSeek={handleSeek} isLive={isLive} />
                              <TimeDisplay currentTime={currentTime} duration={duration} isLive={isLive} />
                            </div>
                            <div className="overflow-y-auto" style={{ maxHeight: 220, scrollbarWidth: "thin", scrollbarColor: "hsl(var(--primary) / 0.2) transparent" }}>
                              <LyricsContent track={nowPlayingTrack} currentTime={currentTime} syncEnabled={syncEnabled} onSeek={handleSeek} />
                            </div>
                          </motion.div>
                        )}

                        {activeTab === "queue" && (
                          <motion.div key="queue" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                            <div className="flex items-center justify-between pt-3 pb-2">
                              <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "hsl(var(--muted-foreground) / 0.5)" }}>{playlist.length} tracks</span>
                              <span className="text-[9px]" style={{ color: "hsl(var(--muted-foreground) / 0.3)" }}>drag · right-click</span>
                            </div>
                            <Reorder.Group axis="y" values={playlist}
                              onReorder={(newOrder) => {
                                const currentId = playlist[trackIndex]?.id;
                                setPlaylist(newOrder);
                                const ni = newOrder.findIndex((t) => t.id === currentId);
                                if (ni !== -1) setTrackIndex(ni);
                              }}
                              className="space-y-0.5 overflow-y-auto pr-0.5"
                              style={{ maxHeight: 280, scrollbarWidth: "thin", scrollbarColor: "hsl(var(--primary) / 0.22) transparent" }}>
                              {playlist.map((t, i) => {
                                const isActive = !activeSearchId && i === trackIndex;
                                return (
                                  <QueueItem key={t.id} track={t} index={i} isActive={isActive} isPlaying={isPlaying}
                                    isBase={BASE_IDS.has(t.id)} onPlay={() => playAt(i)} onContextMenu={(e) => openCtx(e, t, i, "queue")} />
                                );
                              })}
                            </Reorder.Group>
                          </motion.div>
                        )}

                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div key="collapsed"
                  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-full cursor-grab active:cursor-grabbing"
                  style={{ background: "hsl(var(--surface-1) / 0.95)", border: "1px solid hsl(var(--primary) / 0.2)", backdropFilter: "blur(20px)", boxShadow: isPlaying ? "0 0 20px hsl(var(--primary) / 0.25)" : "var(--shadow-sm)" }}
                  onPointerDown={(e) => { pillDraggedRef.current = false; dragControls.start(e); }}
                  onDrag={() => { pillDraggedRef.current = true; }}
                  onClick={() => { if (!pillDraggedRef.current) setIsExpanded(true); }}
                >
                  <motion.div animate={{ rotate: isPlaying ? 360 : 0 }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    className="w-4 h-4 rounded-full shrink-0" style={{ background: "var(--gradient-primary)" }} />
                  <div className="flex items-end gap-[2px] h-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <motion.div key={i} className="w-[2px] rounded-full" style={{ background: "hsl(var(--primary))", height: 16, originY: 1 }}
                        animate={isPlaying ? { scaleY: [0.2, BAR_CONFIGS[i * 3].scaleA, 0.2] } : { scaleY: 0.2 }}
                        transition={isPlaying ? { duration: BAR_CONFIGS[i * 3].duration, repeat: Infinity, ease: "easeInOut", delay: i * 0.07 } : { duration: 0.3 }} />
                    ))}
                  </div>
                  <span className="text-xs font-semibold truncate max-w-[110px]" style={{ color: "hsl(var(--foreground))" }}>{nowPlayingTrack?.title ?? "—"}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </>
  );
}